/**
 * Line vs service-drop classification shared by the visualization transform.
 *
 * A service drop is Type=Drop/Airdac, or a line whose endpoint is a *customer*
 * identity (Survey_ID / Name / …). Connection Pole_ID / poleId is the serving
 * pole label — it must never be treated as a drop endpoint, or every LV trunk
 * into a customer pole paints as a dropline (SETTO PVS_04_CA17–CA12).
 */

export function normalizeLineType(rawType: unknown): string {
  if (typeof rawType !== 'string') {
    return '';
  }
  const trimmed = rawType.trim();
  if (trimmed.length === 0) {
    return '';
  }
  const upper = trimmed.toUpperCase();
  if (upper === 'MV' || upper === 'LV') {
    return upper;
  }
  if (upper === 'DROP' || upper === 'AIRDAC') {
    return 'Drop';
  }
  return trimmed;
}

/** Customer identity fields only — not serving-pole Pole_ID / poleId. */
const CONNECTION_IDENTITY_KEYS = [
  'ID',
  'surveyId',
  'Survey_ID',
  'Survey ID',
  'Name',
] as const;

function addIdentityVariants(ids: Set<string>, value: unknown): void {
  if (value === undefined || value === null) return;
  const id = String(value).trim();
  if (!id) return;
  ids.add(id);
  ids.add(id.replace(/ /g, '_'));
  ids.add(id.replace(/_/g, ' '));
}

export function connectionIdentityLookup(
  connections: Array<{ props?: Record<string, unknown> }>
): Set<string> {
  const ids = new Set<string>();
  for (const connection of connections || []) {
    const props = connection.props || {};
    for (const key of CONNECTION_IDENTITY_KEYS) {
      addIdentityVariants(ids, props[key]);
    }
  }
  return ids;
}

const POLE_IDENTITY_KEYS = ['poleId', 'ID', 'id', 'Pole_ID', 'Pole ID'] as const;

export function poleIdentityLookup(
  poles: Array<{ props?: Record<string, unknown> }>
): Set<string> {
  const ids = new Set<string>();
  for (const pole of poles || []) {
    const props = pole.props || {};
    for (const key of POLE_IDENTITY_KEYS) {
      addIdentityVariants(ids, props[key]);
    }
  }
  return ids;
}

export function lineEndpointIds(line: { props?: Record<string, unknown> }): [string, string] {
  const props = line.props || {};
  const node1 = String(
    props.node1 ?? props['Node 1'] ?? props.Pole_ID_Fr ?? props.DropPoleID ?? ''
  ).trim();
  const node2 = String(
    props.node2 ?? props['Node 2'] ?? props.Pole_ID_To ?? props.DropConnID ?? ''
  ).trim();
  return [node1, node2];
}

function endpointTouchesConnectionId(nodeId: string, connIds: Set<string>): boolean {
  if (!nodeId) return false;
  if (connIds.has(nodeId)) return true;
  if (connIds.has(nodeId.replace(/ /g, '_'))) return true;
  if (connIds.has(nodeId.replace(/_/g, ' '))) return true;
  return false;
}

export function isServiceDropLine(
  line: { props?: Record<string, unknown> },
  connIds: Set<string>,
  poleIds?: Set<string>
): boolean {
  const [node1, node2] = lineEndpointIds(line);
  if (endpointTouchesConnectionId(node1, connIds) || endpointTouchesConnectionId(node2, connIds)) {
    return true;
  }
  // Stale Pole_ID can leave a Type=Drop/Airdac row between two poles (SETTO:
  // customer listed on CA17 but actually hung on CA12). That is a trunk.
  if (
    poleIds &&
    node1 &&
    node2 &&
    endpointTouchesConnectionId(node1, poleIds) &&
    endpointTouchesConnectionId(node2, poleIds)
  ) {
    return false;
  }
  return normalizeLineType(line.props?.type ?? line.props?.Type) === 'Drop';
}
